import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { createContext, useCallback, useContext, useEffect, useMemo, useState, } from 'react';
import { logout as logoutRequest, me } from '@/features/auth';
import { ApiError, setOnUnauthorized } from '@/shared/api';
import { Skeleton } from '@/shared/ui';
const AuthContext = createContext(null);
export function AuthProvider({ children }) {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [bootError, setBootError] = useState(null);
    useEffect(() => {
        setOnUnauthorized(() => {
            setUser(null);
        });
        return () => setOnUnauthorized(null);
    }, []);
    useEffect(() => {
        let cancelled = false;
        me()
            .then((session) => {
            if (!cancelled) {
                setUser(session);
            }
        })
            .catch((error) => {
            if (cancelled) {
                return;
            }
            if (error instanceof ApiError && error.status === 401) {
                setUser(null);
                return;
            }
            setBootError(error instanceof Error ? error.message : String(error));
        })
            .finally(() => {
            if (!cancelled) {
                setLoading(false);
            }
        });
        return () => {
            cancelled = true;
        };
    }, []);
    const logout = useCallback(async () => {
        try {
            await logoutRequest();
        }
        finally {
            setUser(null);
        }
    }, []);
    const value = useMemo(() => ({
        user,
        logout,
    }), [user, logout]);
    if (loading) {
        return (_jsxs("div", { className: "grid min-h-screen place-items-center gap-3 bg-[color:var(--unity-color-page)]", children: [_jsx(Skeleton, { className: "h-10 w-48" }), _jsx("p", { className: "text-sm text-muted-foreground", children: "\u041F\u0440\u043E\u0432\u0435\u0440\u044F\u0435\u043C \u0432\u0445\u043E\u0434\u2026" })] }));
    }
    if (bootError) {
        return (_jsx("div", { className: "grid min-h-screen place-items-center bg-[color:var(--unity-color-page)] px-4", children: _jsxs("p", { className: "max-w-md text-center text-sm text-destructive", children: ["\u041A\u0430\u0431\u0438\u043D\u0435\u0442 \u0441\u0435\u0439\u0447\u0430\u0441 \u043D\u0435 \u043E\u0442\u043A\u0440\u044B\u0432\u0430\u0435\u0442\u0441\u044F. \u041F\u0440\u043E\u0432\u0435\u0440\u044C\u0442\u0435 \u0441\u0435\u0442\u044C \u0438 \u043E\u0431\u043D\u043E\u0432\u0438\u0442\u0435 \u0441\u0442\u0440\u0430\u043D\u0438\u0446\u0443.", bootError ? ` ${bootError}` : ''] }) }));
    }
    return _jsx(AuthContext.Provider, { value: value, children: children });
}
export function useAuth() {
    const ctx = useContext(AuthContext);
    if (!ctx) {
        throw new Error('useAuth must be used within AuthProvider');
    }
    return ctx;
}
